import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {IconAlertTriangle, IconClock} from '@tabler/icons-react';
import React from 'react';

export interface DocumentStateBannerProps {
  pendingVersion?: number;
  isRejected: boolean;
  version?: number;
  resolvedByName?: string;
  resolutionComment?: string;
  /** Whether the current user can request re-approval — gates which recovery guidance is shown on the rejected banner, mirroring `TaskStateBanner`'s permission-branched copy. */
  canRequestReReview: boolean;
  onViewVersions: () => void;
}

/**
 * Document review-state banners — pending-approval and rejected-version.
 * Shaped like `TaskStateBanner` (`Alert` with `icon`/`title`/`color`/
 * `variant="light"`) and rendered once above the tabs rather than nested
 * inside a single tab, so it stays visible regardless of the active tab.
 */
export const DocumentStateBanner: React.FC<DocumentStateBannerProps> = ({
  pendingVersion,
  isRejected,
  version,
  resolvedByName,
  resolutionComment,
  canRequestReReview,
  onViewVersions,
}) => {
  if (!pendingVersion && !isRejected) {
    return null;
  }

  return (
    <>
      {pendingVersion && (
        <Alert
          icon={<IconClock />}
          title="Pending Approval"
          color="yellow"
          variant="light"
          data-testid="pending-version-banner">
          Version {pendingVersion} is awaiting approval —{' '}
          <Anchor size="sm" onClick={onViewVersions}>
            View versions
          </Anchor>
        </Alert>
      )}

      {isRejected && (
        <Alert
          icon={<IconAlertTriangle />}
          title="Rejected"
          color="red"
          variant="light"
          data-testid="rejected-version-banner">
          Version {version} was rejected
          {resolvedByName ? ` by ${resolvedByName}` : ''}
          {resolutionComment ? `: "${resolutionComment}"` : '.'}{' '}
          {canRequestReReview
            ? 'Use the "Request re-approval" button in the page header to resubmit this version for approval.'
            : 'You do not have permissions to request re-approval. Contact someone with edit access to this document.'}
        </Alert>
      )}
    </>
  );
};
